/**
 * Server errors shown beside the field they concern (2026-10-19).
 *
 * The backend answers a bad body in one of two ways (see apiError.js for the
 * envelope):
 *   - 422 `validation_error` with `params.errors[{loc, msg, type}]` — `loc` is
 *     `body.<field>[.<part>]`; `type` is pydantic's (string_too_long…) or our
 *     own (period_years_required…), translated under `errors:fields.types`;
 *   - a coded 400/409 (`case_index_taken`, `due_date_before_registration`…)
 *     whose `params.field` names the field; the sentence is the code's own.
 *
 * `fieldErrors(res)` turns either into `{ field: message }`; anything it can't
 * place is left to the caller (a toast). `codeFields` maps codes that carry no
 * `params.field` to a field; `overrides` ({code: text | (parsed) => text}, as
 * apiErrorMessage takes) words a code's sentence for this form.
 */

import i18n from '../../i18n';
import { apiErrorMessage, parseApiError } from './apiError';

const parsedOf = (input) => {
  if (input && input.errorInfo) return input.errorInfo; // a console/records client result
  if (input && input.code !== undefined && input.params !== undefined && !(input instanceof Error)) return input;
  return parseApiError(input);
};

/** The message for one pydantic error type. */
export function typeMessage(type) {
  const key = `errors:fields.types.${type}`;
  return i18n.exists(key) ? i18n.t(key) : i18n.t('errors:fields.invalid');
}

export function fieldErrors(input, codeFields = {}, overrides = null) {
  const parsed = parsedOf(input);
  if (!parsed) return {};
  const { code, params = {} } = parsed;
  if (code === 'validation_error' && Array.isArray(params.errors)) {
    const out = {};
    params.errors.forEach((error) => {
      const loc = String(error.loc || '').replace(/^(body|query)\.?/, '');
      if (!loc || out[loc]) return;
      out[loc] = typeMessage(error.type);
    });
    return out;
  }
  const field = params.field || codeFields[code];
  return field ? { [field]: apiErrorMessage(parsed, overrides) } : {};
}

/**
 * The usual submit tail: `errors` holds what found one of `fields` (a nested
 * loc such as `title.ru` also marks `title`); `message` is a sentence for a
 * toast when something could not be placed (null otherwise).
 */
export function splitErrors(input, fields, codeFields = {}, overrides = null) {
  const parsed = parsedOf(input);
  const placed = fieldErrors(parsed, codeFields, overrides);
  const errors = {};
  let unplaced = Object.keys(placed).length === 0;
  Object.entries(placed).forEach(([loc, message]) => {
    const field = fields.find((f) => loc === f || loc.startsWith(`${f}.`));
    if (!field) {
      unplaced = true;
      return;
    }
    if (!errors[field]) errors[field] = message;
    if (loc !== field && !errors[loc]) errors[loc] = message;
  });
  let message = null;
  if (unplaced) {
    message = parsed?.code === 'validation_error' ? i18n.t('errors:validation_error') : apiErrorMessage(parsed, overrides);
  }
  return { errors, message };
}

/** Trim every string of a flat object (a form) before it is sent. */
export function trimmed(values) {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]));
}
